<script lang="ts" setup>
import { useAppStore } from "@/store/app";
import { getOptionIconVNode } from "@/util/conditional-logic-helpers";
import {
  ArrowCircleUpRightIcon,
  ArrowNarrowLeftIcon,
  Contrast01Icon,
  Dataflow03Icon,
  Edit05Icon,
  Globe06Icon,
  InfoCircleIcon,
  Menu05Icon,
  UserX01Icon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIButton,
  UIModal,
  UIModalContent,
  UIModalHeader,
  UISelect,
  UISpace,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { computed, h, ref, VNode } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import ActionButton from "./ActionButton.vue";
import AlertModal from "./AlertModal.vue";
import ConditionCard from "./ConditionCard.vue";
import ConditionIcons from "./ConditionIcons.vue";
import DisplayCustomMessage from "./DisplayCustomMessage.vue";
const { t } = useI18n();

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const store = useAppStore();
const editIndex = ref(0);
const showHomePage = ref(true);
const showDisplayCustomMessage = ref(false);
const showRedirectToUrl = ref(false);
const showDisqualifyLead = ref(false);
const showHideFields = ref(false);
const showJumpTo = ref(false);
const enableDeleteModal = ref(false);

const emit = defineEmits(["update:closeModal"]);
/**
 * @description Keeps every return-to-list path in sync so a previously opened
 * editor is not left visible behind the conditional-logic home screen.
 */
const resetEditorState = () => {
  showHomePage.value = true;
  showDisplayCustomMessage.value = false;
  showRedirectToUrl.value = false;
  showHideFields.value = false;
  showDisqualifyLead.value = false;
  showJumpTo.value = false;
};

const closeModal = () => emit("update:closeModal");
// Reusable option renderer (same as used for show/hide: tooltip over option label)
const renderOptions = ({ node, option }: { node: VNode; option: any }) =>
  h(
    UITooltip,
    { placement: "left-start" },
    {
      trigger: () => node,
      default: () => option.label,
    }
  );

// Show/Hide-like option renderer with icon (no HTML injection)
const resolveIconVNode = (option: any): VNode | null => {
  try {
    const maybe = typeof option.icon === "function" ? option.icon() : option.icon;
    return maybe || null;
  } catch {
    return null;
  }
};

const renderFormattedOption = ({ node, option }: { node: VNode; option: any }) =>
  h("div", { class: "relative flex items-center w-full" }, [
    h(
      "div",
      {
        class: ["ml-2 shrink-0 flex items-center justify-center w-6 h-6"].join(" "),
      },
      [resolveIconVNode(option)]
    ),
    h("div", { class: "w-full" }, [node]),
  ]);

// Build filter values options in the same format as Show/Hide when mode=field
const filterValuesOptions = computed(() => {
  const mode = store.conditionalLogicFilter?.mode || "field";
  if (mode === "field") {
    const allFields = store.slides.flatMap((s) => s.slideData || []);
    const flattened = allFields.flatMap((item: any) => {
      const isGroup = String(item?.type || "") === "group";
      if (!isGroup) {
        return [item];
      }
      if (Array.isArray(item?.children) && item.children.length) {
        return item.children;
      }
      if (String(item?.tag || "") === "group_address") {
        const fallback =
          (store as any)?.form?.address?.children || (store as any)?.addressSettings?.children;
        if (Array.isArray(fallback) && fallback.length) {
          return fallback;
        }
      }
      return [];
    });
    return flattened
      .filter(
        (item: any) =>
          !(String(item?.type || "") === "group" || String(item?.tag || "") === "group_address")
      )
      .filter(
        (item: any) => typeof item?.hiddenFieldQueryKey === "string" && item.hiddenFieldQueryKey
      )
      .map((item: any) => {
        const iconRenderer = () => getOptionIconVNode(item);
        const plainLabel = (item.label || "").replace(/<\/?[^>]+(>|$)/g, "");
        return {
          value: item.hiddenFieldQueryKey,
          label: plainLabel,
          type: item.type,
          icon: iconRenderer,
        };
      });
  }
  // Slide mode
  return store.slides.map((s) => ({ value: s.id, label: s.slideName }));
});

const conditionButtons = [
  {
    color: "success",
    buttonText: t("form.conditonalLogic.redirectToUrl"),
    subButtonText: t("form.conditonalLogic.redirectToUrlSubText"),
    type: "redirectToUrl",
    icon: "Globe06Icon",
    iconClass: "text-success-600",
  },
  {
    color: "primary",
    buttonText: t("form.conditonalLogic.displayCustomMessage"),
    subButtonText: t("form.conditonalLogic.displayCustomMessageSubText"),
    type: "displayCustomMessage",
    icon: "Edit05Icon",
    iconClass: "text-primary-600",
  },
  {
    color: "error",
    buttonText: t("form.conditonalLogic.disqualifyLead"),
    subButtonText: t("form.conditonalLogic.disqualifyLeadSubText"),
    type: "disqualifyLead",
    icon: "UserX01Icon",
    iconClass: "text-error-600",
  },
  {
    color: "warning",
    buttonText: t("form.conditonalLogic.showHideFields"),
    subButtonText: t("form.conditonalLogic.showHideFieldsSubText"),
    type: "showHideFields",
    icon: "Contrast01Icon",
    iconClass: "text-warning-600",
  },
  {
    color: "violet",
    buttonText: t("form.conditonalLogic.jumpTo"),
    subButtonText: t("survey.jumpToSubText"),
    type: "jumpTo",
    icon: "ArrowCircleUpRightIcon",
    iconClass: "text-violet-600",
  },
];

const availableConditionButtons = computed(() => {
  if (store.quizId) {
    return conditionButtons.filter((button) => button.type === "redirectToUrl");
  }

  return conditionButtons.filter(
    (button) => button.type !== "jumpTo" || (button.type === "jumpTo" && store.surveyId)
  );
});

const getIconComponent = (iconName) => {
  switch (iconName) {
    case "Globe06Icon":
      return Globe06Icon;
    case "Edit05Icon":
      return Edit05Icon;
    case "UserX01Icon":
      return UserX01Icon;
    case "Contrast01Icon":
      return Contrast01Icon;
    case "ArrowCircleUpRightIcon":
      return ArrowCircleUpRightIcon;
    default:
      return null;
  }
};

const goToHomePage = () => {
  store.builderConfig.conditionalLogic.forEach((item) => {
    item.conditions = item.conditions?.filter((condition: any) => {
      if (condition?.selectedField == null) {
        return false;
      }
      if ((condition as any)?.fieldMode === "slide") {
        return true;
      }
      return condition?.selectedOperation != null;
    });
  });
  // Remove placeholder/null rules
  store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter(
    (item: any) => item && item.outcome !== null && item.conditions !== null
  );

  const current = store.builderConfig.conditionalLogic[editIndex.value];
  const outcome = current?.outcome as any;
  const isEmpty = !outcome
    ? true
    : outcome.type === "jumpTo"
    ? !outcome.jumpTarget
    : outcome.type === "showHideFields"
    ? Array.isArray(outcome.value)
      ? outcome.value.length === 0
      : !outcome.value
    : !outcome.value;
  if (isEmpty) {
    deleteStep(editIndex.value, false);
  }

  resetEditorState();
};

const goToDisplayCustomMessage = (condition, type) => {
  const stepIndex = store.builderConfig.conditionalLogic?.findIndex((c) => c === condition);
  if (condition !== null) {
    editIndex.value = stepIndex;
  } else {
    if (!store.builderConfig.conditionalLogic) {
      store.builderConfig.conditionalLogic = [];
    }
    editIndex.value =
      store.builderConfig.conditionalLogic.push({
        conditionalOperation: null,
        conditions: null,
        outcome: null,
      }) - 1;
  }
  showHomePage.value = false;
  if (type === "displayCustomMessage") {
    showDisplayCustomMessage.value = true;
  } else if (type === "redirectToUrl") {
    showRedirectToUrl.value = true;
  } else if (type === "disqualifyLead") {
    showDisqualifyLead.value = true;
  } else if (type === "showHideFields") {
    showHideFields.value = true;
  } else if (type === "jumpTo") {
    showJumpTo.value = true;
  }
};

const deleteStep = (stepIndex, showUnsavedChanges = true) => {
  store.builderConfig.conditionalLogic.splice(stepIndex, 1);
  store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter(
    (item: any) => item.outcome !== null
  );
  if (showUnsavedChanges) {
    store.anyUnsavedChanges = true;
  }
};

const onSave = () => {
  // Remove placeholder/null rules on save as well
  store.builderConfig.conditionalLogic = store.builderConfig.conditionalLogic.filter(
    (item: any) => item && item.outcome !== null && item.conditions !== null
  );
  resetEditorState();
};

const cancelCallbackDeleteUpdate = () => {
  enableDeleteModal.value = false;
};

const successCallbackDeleteUpdate = () => {
  // If deleting a Jump To rule, also nullify the related slide.jumpTo if linked
  const rule = store.builderConfig.conditionalLogic?.[editIndex.value];
  if (rule?.outcome?.type === "jumpTo" && store.surveyId) {
    // Determine the slide IF used by the rule (slide-based or by matching slide id)
    const slideIds = store.slides.map((s) => String(s.id));
    const sid = String(rule?.conditions?.[0]?.selectedField || "");
    const slideId =
      (rule?.conditions?.[0] as any)?.fieldMode === "slide" || slideIds.includes(sid) ? sid : null;
    if (slideId) {
      const slideIndex = store.slides.findIndex((s) => s.id === slideId);
      if (slideIndex >= 0) {
        (store.slides[slideIndex] as any).jumpTo = null;
      }
    }
  }
  deleteStep(editIndex.value);
  enableDeleteModal.value = false;
};

const showDeleteModal = (condition) => {
  const stepIndex = store.builderConfig.conditionalLogic?.findIndex((c) => c === condition);
  editIndex.value = stepIndex;
  enableDeleteModal.value = true;
};

// Hide filters when there are no conditions
const hasAnyConditions = computed(() => {
  const rules: any[] = (store.builderConfig as any)?.conditionalLogic || [];
  return rules.some((r) => r && r.outcome !== null && r.conditions !== null);
});

// Map fieldKey -> slideId for faster lookups
const fieldKeyToSlideIdMap = computed(() => {
  const map = new Map<string, string>();
  store.slides.forEach((slide: any) => {
    if (Array.isArray(slide?.slideData)) {
      slide.slideData.forEach((f: any) => {
        const key = f?.hiddenFieldQueryKey;
        if (typeof key === "string" && key) {
          map.set(key, String(slide.id));
        }
      });
    }
  });
  return map;
});

const getSlideIdForField = (fieldKey?: string | null): string | null => {
  if (!fieldKey) {
    return null;
  }
  return fieldKeyToSlideIdMap.value.get(fieldKey) || null;
};

const mapConditionNames = {
  displayCustomMessage: { name: t("form.conditonalLogic.displayCustomMessage"), color: "primary" },
  redirectToUrl: { name: t("form.conditonalLogic.redirectToUrl"), color: "success" },
  disqualifyLead: { name: t("form.conditonalLogic.disqualifyLead"), color: "error" },
  showHideFields: { name: t("form.conditonalLogic.showHideFields"), color: "warning" },
  jumpTo: { name: t("form.conditonalLogic.jumpTo"), color: "violet" },
};

const showHideConditionsFlag = (condition) => condition.outcome?.type === "showHideFields";
const otherConditionsFlag = (condition) => condition.outcome?.type !== "showHideFields";

const showHideConditions = computed({
  get: () =>
    store.builderConfig.conditionalLogic?.filter(showHideConditionsFlag)?.filter((condition) => {
      if (!store.conditionalLogicFilter) {
        return true;
      }
      const { mode, values } = store.conditionalLogicFilter;
      if (!values?.length) {
        return true;
      }
      if (mode === "field") {
        return condition?.conditions?.some((c) => values.includes(c.selectedField));
      }
      if (mode === "slide") {
        const inIF = condition?.conditions?.some((c) => {
          const slideId = getSlideIdForField(c.selectedField);
          return slideId ? values.includes(slideId) : false;
        });
        return !!inIF;
      }
      return true;
    }),
  set: (newShowHideConditions) => {
    store.builderConfig.conditionalLogic = [
      ...newShowHideConditions,
      ...otherConditions.value.filter(otherConditionsFlag),
    ];
  },
});

const otherConditions = computed({
  get: () =>
    store.builderConfig.conditionalLogic?.filter(otherConditionsFlag)?.filter((condition) => {
      // Apply optional filter by field/slide if set
      if (!store.conditionalLogicFilter) {
        return true;
      }
      const { mode, values } = store.conditionalLogicFilter;
      if (!values?.length) {
        return true;
      }
      if (mode === "field") {
        return condition?.conditions?.some((c) => values.includes(c.selectedField));
      }
      if (mode === "slide") {
        // 1) Match outcome.jumpTarget (for jumpTo)
        const inOutcomeJump =
          condition?.outcome?.jumpTarget && values.includes(condition.outcome.jumpTarget);
        // 2) Any IF field belongs to any selected slide
        const inIF = condition?.conditions?.some((c) => {
          const slideId = getSlideIdForField(c.selectedField);
          return slideId ? values.includes(slideId) : false;
        });
        // 3) For Show/Hide, any target field belongs to selected slides
        const out: any = condition?.outcome || ({} as any);
        const rawTarget = (out as any)?.value as any;
        const outcomeTargets = Array.isArray(rawTarget)
          ? (rawTarget as any[])
          : [rawTarget].filter(Boolean);
        const inOutcomeTargets = outcomeTargets.some((q) => {
          const slideId = getSlideIdForField(q);
          return slideId ? values.includes(slideId) : false;
        });
        return !!(inOutcomeJump || inIF || inOutcomeTargets);
      }
      return true;
    }),
  set: (newOtherConditions) => {
    store.builderConfig.conditionalLogic = [
      ...showHideConditions.value.filter(showHideConditionsFlag),
      ...newOtherConditions,
    ];
  },
});

const displayCustomMessageType = computed(() => {
  if (showDisplayCustomMessage.value) {
    return "displayCustomMessage";
  }
  if (showRedirectToUrl.value) {
    return "redirectToUrl";
  }
  if (showDisqualifyLead.value) {
    return "disqualifyLead";
  }
  if (showHideFields.value) {
    return "showHideFields";
  }
  if (showJumpTo.value) {
    return "jumpTo";
  }
  return "";
});
</script>

<template>
  <UIModal :show="show" :width="800" @update:show="closeModal">
    <template #header>
      <UIModalHeader id="modal-custom-header" type="custom">
        <template #customHeader>
          <UISpace class="py-2" justify="space-between" align="center">
            <Dataflow03Icon v-if="showHomePage" class="h-6 w-6 text-gray-700" />
            <ArrowNarrowLeftIcon
              v-else
              class="h-6 w-6 cursor-pointer text-gray-700"
              @click="goToHomePage"
            />
            <XCloseIcon class="h-5 w-5 cursor-pointer text-gray-700" @click="closeModal" />
          </UISpace>
          <div v-if="showHomePage">
            <div class="pb-2">{{ t("form.conditonalLogic.conditions") }}</div>
            <span class="text-base font-medium">{{
              t("form.conditonalLogic.addNewCondition")
            }}</span>
            <div class="mt-2 flex flex-wrap gap-x-1.5 gap-y-1.5">
              <ActionButton
                v-for="(button, index) in availableConditionButtons"
                :key="index"
                :color="button.color"
                :button-text="button.buttonText"
                :sub-button-text="button.subButtonText"
                class="w-[calc(50%-0.1875rem)]"
                @on-action-button-click="() => goToDisplayCustomMessage(null, button.type)"
              >
                <component
                  :is="getIconComponent(button.icon)"
                  class="h-6 w-6"
                  :class="button.iconClass"
                />
              </ActionButton>
            </div>
          </div>
          <div v-else-if="showDisplayCustomMessage" class="pb-2">
            {{ t("form.conditonalLogic.displayCustomMessage") }}
          </div>
          <div v-else-if="showRedirectToUrl" class="pb-2">
            {{ t("form.conditonalLogic.redirectToUrl") }}
          </div>
          <div v-else-if="showDisqualifyLead" class="pb-2">
            {{ t("form.conditonalLogic.disqualifyLead") }}
          </div>
          <div v-else-if="showHideFields" class="pb-2">
            {{ t("form.conditonalLogic.showHideFields") }}
          </div>
          <div v-else-if="showJumpTo" class="pb-2">
            {{ t("form.conditonalLogic.jumpTo") }}
          </div>
        </template>
      </UIModalHeader>
    </template>
    <UIModalContent>
      <div v-if="!showHomePage" class="divider" />
      <div
        v-if="showHomePage && !store.builderConfig.conditionalLogic?.length"
        class="flex flex-col items-center justify-center p-6"
      >
        <span class="text-base font-semibold">{{
          t("form.conditonalLogic.noExistingCondition")
        }}</span>
        <span class="text-gray-500">{{ t("form.conditonalLogic.freeRoamMessage") }}</span>
        <img
          src="@/assets/img/no-conditions.png"
          class="p-6"
          :alt="t('form.conditonalLogic.noConditionsAlt')"
        />
      </div>
      <DisplayCustomMessage
        v-if="
          showDisplayCustomMessage ||
          showRedirectToUrl ||
          showDisqualifyLead ||
          showHideFields ||
          showJumpTo
        "
        :type="displayCustomMessageType"
        :edit-index="editIndex"
        @on-save="onSave" />
      <!-- Filters between action buttons and cards (content area) -->
      <div v-if="showHomePage && store.surveyId && hasAnyConditions" class="mb-3 mt-3 flex gap-2">
        <UISelect
          id="conditional_logic-select-mode"
          class="w-40"
          :value="store.conditionalLogicFilter?.mode || 'field'"
          :options="[
            { label: t('form.conditonalLogic.field'), value: 'field' },
            { label: t('form.conditonalLogic.slide'), value: 'slide' },
          ]"
          :render-option="renderOptions"
          :aria-label="t('form.conditonalLogic.selectFieldOrSlide')"
          @update:value="(v) => (store.conditionalLogicFilter = { mode: v, values: [] })"
        />
        <UISelect
          id="conditional_logic-select-values"
          class="flex-1"
          :multiple="true"
          :value="store.conditionalLogicFilter?.values || []"
          :options="filterValuesOptions"
          :placeholder="t('common.filter')"
          filterable
          :render-option="
            (store.conditionalLogicFilter?.mode || 'field') === 'field'
              ? renderFormattedOption
              : renderOptions
          "
          :aria-label="t('common.filter')"
          @update:value="
            (v) =>
              (store.conditionalLogicFilter = {
                mode: store.conditionalLogicFilter?.mode || 'field',
                values: v,
              })
          "
        />
      </div>

      <div v-if="showHomePage" class="max-h-[460px] overflow-auto rounded-lg border">
        <div v-if="showHideConditions?.length">
          <VueDraggableNext
            v-model="showHideConditions"
            handle=".drag-handle"
            @change="store.anyUnsavedChanges = true"
          >
            <div v-for="(condition, index) in showHideConditions" :key="index">
              <div
                :class="
                  index !== showHideConditions?.length - 1
                    ? 'divider'
                    : otherConditions?.length
                      ? 'divider2'
                    : ''
                "
              >
                <ConditionCard
                  :color="mapConditionNames[condition.outcome?.type]?.color"
                  :button-text="mapConditionNames[condition.outcome?.type]?.name"
                  :condition="condition"
                  @on-edit-button-click="
                    () => goToDisplayCustomMessage(condition, condition.outcome?.type)
                  "
                  @on-delete-button-click="() => showDeleteModal(condition)"
                >
                  <ConditionIcons :type="condition.outcome.type" />
                  <template #handle>
                    <div class="drag-handle cursor-pointer">
                      <Menu05Icon class="h-4 w-4" />
                    </div>
                  </template>
                </ConditionCard>
              </div>
            </div>
          </VueDraggableNext>
        </div>
        <div v-if="otherConditions?.length">
          <VueDraggableNext
            v-model="otherConditions"
            handle=".drag-handle"
            @change="store.anyUnsavedChanges = true"
          >
            <div v-for="(condition, index) in otherConditions" :key="index">
              <div :class="index !== otherConditions?.length - 1 ? 'divider' : ''">
                <ConditionCard
                  :color="mapConditionNames[condition.outcome?.type]?.color"
                  :button-text="mapConditionNames[condition.outcome?.type]?.name"
                  :condition="condition"
                  @on-edit-button-click="
                    () => goToDisplayCustomMessage(condition, condition.outcome?.type)
                  "
                  @on-delete-button-click="() => showDeleteModal(condition)"
                >
                  <ConditionIcons :type="condition.outcome.type" />
                  <template #handle>
                    <div class="drag-handle cursor-pointer">
                      <Menu05Icon class="h-4 w-4" />
                    </div>
                  </template>
                </ConditionCard>
              </div>
            </div>
          </VueDraggableNext>
        </div>
      </div>
      <div
        v-if="showHomePage"
        class="my-1.5 flex items-center gap-x-1.5 text-xs leading-5 text-gray-600"
      >
        <InfoCircleIcon class="h-4 w-4" />
        <div>{{ t("form.conditonalLogic.conditionExecutedMessage") }}</div>
      </div>
      <div v-if="showHomePage" class="divider" />
      <div v-if="showHomePage" class="flex justify-end pt-4">
        <UIButton id="conditional-logic-save-close" type="primary" @click="closeModal">
          {{ t("common.save") }}
        </UIButton>
      </div>
    </UIModalContent>
  </UIModal>
  <AlertModal
    v-if="enableDeleteModal"
    :description="t('form.conditonalLogic.alertConditionDelete')"
    :title="t('form.conditonalLogic.deleteCondition')"
    :positive-text="t('common.delete')"
    :negative-text="t('common.cancel')"
    :show-modal-popup="enableDeleteModal"
    header-type="error"
    footer-type="error"
    @pop:change="cancelCallbackDeleteUpdate"
    @pop:positive="successCallbackDeleteUpdate"
    @pop:negative="cancelCallbackDeleteUpdate"
  />
</template>

<style scoped>
.divider {
  border-bottom: 1px solid #eaecf0;
  width: 100%;
}

.divider2 {
  border-bottom: 3px solid #eaecf0;
  width: 100%;
}
</style>
